import { useEffect } from 'react';
import { LayoutHeader as Header } from '@/layouts';
import { LayoutFooter as Footer } from '@/layouts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Shield, FileText, Calendar, Mail, Phone, MapPin } from 'lucide-react';
import { useAppData } from '@/contexts/AppDataContext';
import { useStore } from '@/contexts/StoreContext';

import { Layout3Root } from './Layout3Root';

const PrivacyPolicy = () => {
  const { config } = useAppData();
  const { selectedStore } = useStore();
  const store = selectedStore || config?.stores?.[0];

  useEffect(() => {
    document.title = `Política de Privacidade - ${config?.name || 'Nossa Loja'}`;
  }, [config?.name]);

  const formatDate = () => {
    return new Date().toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatOpeningHours = () => {
    const hours = store?.openingHours?.default;
    if (!hours) return 'Segunda a Sexta, 8h às 18h | Sábado, 8h às 12h';

    const weekDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];

    // Check if all weekdays have the same hours
    const weekdayHours = weekDays.map(day => hours[day]);
    const allWeekdaysSame = weekdayHours.every(
      (h, i, arr) => h?.from === arr[0]?.from && h?.to === arr[0]?.to
    );

    if (allWeekdaysSame && hours.monday?.from && hours.monday?.to) {
      const weekdayTime = `${hours.monday.from} - ${hours.monday.to}`;
      
      if (hours.saturday?.from && hours.saturday?.to) {
        const saturdayTime = `${hours.saturday.from} - ${hours.saturday.to}`;
        return `Segunda a Sexta, ${weekdayTime} | Sábado, ${saturdayTime}`;
      } else {
        return `Segunda a Sexta, ${weekdayTime}`;
      }
    }

    // Fallback
    return 'Consulte horários disponíveis';
  };

  return (
    <Layout3Root className="bg-gradient-to-br from-background via-muted/30 to-background">
      <Header config={config} />

      <div className="container mx-auto px-4 pt-[120px] pb-16">
        {/* Hero Section */}
        <section className="py-12 text-center">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="p-3 bg-primary/10 rounded-2xl">
                <Shield className="h-8 w-8 text-primary" />
              </div>
              <div>
                <h1 className="text-4xl md:text-5xl font-bold text-foreground">
                  Política de Privacidade
                </h1>
                <p className="text-xl text-muted-foreground mt-2">
                  Transparência e proteção dos seus dados
                </p>
              </div>
            </div>
            
            <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground">
              <Badge variant="outline" className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Última atualização: {formatDate()}
              </Badge>
              <Badge variant="outline" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Documento legal
              </Badge>
            </div>
          </div>
        </section>

        {/* Company Information */}
        <section className="mb-12">
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-primary" />
                Informações da Empresa
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold text-foreground mb-2">Loja:</h4>
                  <p className="text-muted-foreground">{store?.company || config?.name || 'Nome da Empresa'}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-foreground mb-2">CNPJ:</h4>
                  <p className="text-muted-foreground font-mono">{store?.document || 'XX.XXX.XXX/XXXX-XX'}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-foreground mb-2">Endereço:</h4>
                  <p className="text-muted-foreground">{store?.place?.address || 'Endereço da empresa'}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-foreground mb-2">Contato:</h4>
                  <div className="space-y-1">
                    <p className="text-muted-foreground flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      {store?.phone?.formatted || '(XX) XXXXX-XXXX'}
                    </p>
                    <p className="text-muted-foreground flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      {config?.email || 'contato@empresa.com'}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Privacy Policy Content */}
        <section className="max-w-4xl mx-auto space-y-8">
          <Card className="border-border/50">
            <CardContent className="p-8">
              <div className="prose prose-slate max-w-none">
                
                <h2 className="text-2xl font-bold text-foreground mb-4">1. Introdução</h2>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  Esta Política de Privacidade descreve como {store?.company || config?.name || 'nossa empresa'} 
                  ("nós", "nosso" ou "empresa") coleta, usa, armazena e protege suas informações pessoais quando você 
                  utiliza nosso site e serviços relacionados à venda de veículos seminovos e zero quilômetro.
                </p>

                <h2 className="text-2xl font-bold text-foreground mb-4">2. Informações que Coletamos</h2>
                <div className="space-y-4 mb-6">
                  <h3 className="text-lg font-semibold text-foreground">2.1 Informações Fornecidas por Você:</h3>
                  <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                    <li>Nome completo, e-mail, telefone e endereço</li>
                    <li>Informações para financiamento (CPF, RG, comprovantes de renda)</li>
                    <li>Preferências de veículos e dados de interesse</li>
                    <li>Comunicações através de formulários de contato</li>
                  </ul>
                  
                  <h3 className="text-lg font-semibold text-foreground">2.2 Informações Coletadas Automaticamente:</h3>
                  <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                    <li>Dados de navegação e uso do site (cookies, IP, dispositivo)</li>
                    <li>Histórico de veículos visualizados e favoritados</li>
                    <li>Interações com chatbots e formulários</li>
                    <li>Informações de localização (quando autorizado)</li>
                  </ul>
                </div>

                <h2 className="text-2xl font-bold text-foreground mb-4">3. Como Utilizamos suas Informações</h2>
                <div className="space-y-4 mb-6">
                  <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                    <li>Processar solicitações de financiamento e aprovação de crédito</li>
                    <li>Entrar em contato sobre veículos de interesse</li>
                    <li>Personalizar conteúdo e recomendações</li>
                    <li>Melhorar nossos serviços e experiência do usuário</li>
                    <li>Cumprir obrigações legais e regulatórias</li>
                    <li>Enviar comunicações promocionais (com seu consentimento)</li>
                  </ul>
                </div>

                <h2 className="text-2xl font-bold text-foreground mb-4">4. Compartilhamento de Informações</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Compartilhamos suas informações apenas nas seguintes situações:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4 mb-6">
                  <li>Instituições financeiras para análise de crédito</li>
                  <li>Prestadores de serviços terceirizados (com acordos de confidencialidade)</li>
                  <li>Autoridades competentes quando exigido por lei</li>
                  <li>Parceiros comerciais (apenas com seu consentimento explícito)</li>
                </ul>

                <h2 className="text-2xl font-bold text-foreground mb-4">5. Cookies e Tecnologias Similares</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Utilizamos cookies para melhorar sua experiência, incluindo:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4 mb-6">
                  <li><strong>Cookies Essenciais:</strong> Necessários para o funcionamento do site</li>
                  <li><strong>Cookies de Desempenho:</strong> Coletam informações sobre como você usa o site</li>
                  <li><strong>Cookies de Funcionalidade:</strong> Lembram suas preferências</li>
                  <li><strong>Cookies de Marketing:</strong> Personalizam anúncios (apenas com consentimento)</li>
                </ul>

                <h2 className="text-2xl font-bold text-foreground mb-4">6. Segurança dos Dados</h2>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  Implementamos medidas técnicas e organizacionais adequadas para proteger suas informações, 
                  incluindo criptografia, controles de acesso e monitoramento de segurança. No entanto, 
                  nenhum método de transmissão pela internet é 100% seguro.
                </p>

                <h2 className="text-2xl font-bold text-foreground mb-4">7. Seus Direitos</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  De acordo com a LGPD, você tem direito a:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4 mb-6">
                  <li>Acesso aos seus dados pessoais</li>
                  <li>Correção de dados incompletos ou incorretos</li>
                  <li>Exclusão de dados desnecessários</li>
                  <li>Portabilidade dos dados</li>
                  <li>Revogação do consentimento</li>
                  <li>Informações sobre compartilhamento de dados</li>
                </ul>

                <h2 className="text-2xl font-bold text-foreground mb-4">8. Retenção de Dados</h2>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  Mantemos suas informações pelo tempo necessário para cumprir os propósitos descritos nesta política, 
                  respeitando prazos legais e regulatórios. Dados de navegação são mantidos por até 12 meses, 
                  enquanto informações de clientes são preservadas conforme exigido por lei.
                </p>

                <h2 className="text-2xl font-bold text-foreground mb-4">9. Alterações nesta Política</h2>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  Podemos atualizar esta política periodicamente. Alterações significativas serão comunicadas 
                  através do nosso site ou por e-mail. Recomendamos revisar esta política regularmente.
                </p>

                <h2 className="text-2xl font-bold text-foreground mb-4">10. Contato</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Para exercer seus direitos ou esclarecer dúvidas sobre esta política, entre em contato:
                </p>
                <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                  <p className="text-muted-foreground">
                    <strong>E-mail:</strong> {config?.email || 'privacidade@empresa.com'}
                  </p>
                  <p className="text-muted-foreground">
                    <strong>Telefone:</strong> {store?.phone?.formatted || '(XX) XXXXX-XXXX'}
                  </p>
                  <p className="text-muted-foreground">
                    <strong>Endereço:</strong> {store?.place?.address || 'Endereço da empresa'}
                  </p>
                  <p className="text-muted-foreground">
                    <strong>Horário de funcionamento:</strong> {formatOpeningHours()}
                  </p>
                </div>

              </div>
            </CardContent>
          </Card>
        </section>
      </div>

      <Footer config={config} />
    </Layout3Root>
  );
};

export default PrivacyPolicy;
